import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js/pure';
import {
  Elements,
  CardElement,
  useElements,
  useStripe,
  CardElementProps,
} from '@stripe/react-stripe-js';
import worldCountries from 'world-countries';
import { useShoppingCart } from '../context/ShoppingCartProvider';
import { toast } from 'react-toastify';
import { FullscreenSpinner } from '../components/FullscreenSpinner';
import { customFetch } from '../util/functions';
import { Button, ButtonColorSchema } from 'ui-components/build';
import styled from 'styled-components';
import { Input } from 'ui-components/build';
import { BORDER_COLOR } from '../util/styles';
import { Money } from 'shared/build/Money';
import { useHistory } from 'react-router-dom';
import { useAuthentication } from '../context/AuthenticationProvider';
import { Select } from 'ui-components/build';
import * as Sentry from '@sentry/react';
import { PaymentMethods } from 'shared/build/enums';

loadStripe.setLoadParameters({
  advancedFraudSignals: false,
});

const getStripeKey = (paymentMethod: PaymentMethods): string => {
  switch (paymentMethod) {
    case PaymentMethods.StripeBR:
      return process.env.REACT_APP_STRIPE_KEY_BR || '';
    case PaymentMethods.StripePT:
      return process.env.REACT_APP_STRIPE_KEY_PT || '';
    default:
      throw new Error('Payment method is not Stripe related!');
  }
};

const countryList = worldCountries
  .map((country) => ({
    name:
      (country.translations['por'] && country.translations['por'].common) ||
      country.name.common,
    code: country.cca2,
  }))
  .sort((countryA, countryB) => {
    if (countryA.name > countryB.name) return 1;
    if (countryA.name < countryB.name) return -1;

    return 0;
  });

const ScForm = styled.form`
  width: 100%;
  max-width: 500px;
`;

const ScIntroSentence = styled.div`
  h4 {
    margin: 0 0 10px 0;
  }

  p {
    margin: 0 0 20px 0;
  }
`;

const ScWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ScInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;

  input.max-len {
    width: calc(100% - 40px);
  }
`;

const ScSelect = styled(Select)`
  width: 200px;
`;

const ScCardElement = styled(CardElement)`
  border: 2px solid ${BORDER_COLOR};
  border-radius: 4px;
  padding: 11px 8px;
`;

export const CheckoutStripe = ({
  paymentMethod,
}: {
  paymentMethod: PaymentMethods;
}) => {
  const { shoppingCart } = useShoppingCart();
  const history = useHistory();
  const [secret, setSecret] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (!shoppingCart.revision) return;

      const response = await customFetch(
        `/api/website/checkout/start/${shoppingCart.revision}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            paymentMethod,
          }),
        }
      );

      if (!response.ok) {
        Sentry.captureException(new Error('Failed to start closing.'));
        return history.push('/carrinho');
      }

      const secret = await response.json();

      setSecret(secret.clientSecret);
    })();
  }, [shoppingCart.revision, history, paymentMethod]);

  if (!secret) return <FullscreenSpinner show={true} />;

  return (
    <Elements stripe={loadStripe(getStripeKey(paymentMethod))}>
      <CheckoutForm clientSecret={secret} paymentMethod={paymentMethod} />
    </Elements>
  );
};

const CheckoutForm = ({
  clientSecret,
  paymentMethod,
}: {
  clientSecret: string;
  paymentMethod: PaymentMethods;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { shoppingCart } = useShoppingCart();
  const { user } = useAuthentication();
  const history = useHistory();

  const [processing, setProcessing] = useState<boolean>(false);

  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [country, setCountry] = useState<string>('BR');
  const [line1, setLine1] = useState<string>('');
  const [line2, setLine2] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [cardCompleted, setCardCompleted] = useState<boolean>(false);

  const handleChange: CardElementProps['onChange'] = (event) => {
    setCardCompleted(event.complete);

    event.error &&
      toast.error(event.error.message, {
        autoClose: false,
      });
  };

  const handleSubmit: React.DOMAttributes<HTMLFormElement>['onSubmit'] = async (
    e
  ) => {
    e.preventDefault();

    const card = elements?.getElement(CardElement);
    if (!card || !stripe) return;

    setProcessing(true);

    const { paymentIntent, error } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card,
          billing_details: {
            name,
            address: {
              city,
              country,
              line1,
              line2: line2 || undefined,
              state,
            },
          },
        },
        receipt_email: email || undefined,
      }
    );

    if (error) {
      setProcessing(false);
      return toast.error(error.message, {
        autoClose: false,
      });
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      const response = await customFetch(`/api/website/checkout/end`, {
        method: 'PUT',
        body: JSON.stringify({
          paymentIntent,
          email,
          paymentMethod,
        }),
      });

      setProcessing(false);
      history.push('/obrigado');

      if (!response.ok) {
        throw new Error('Payment received but ping was not successful!!!');
      }
    }
  };

  useEffect(() => {
    if (!user) return;

    if (user.email) {
      setEmail(user.email);
    }
  }, [user]);

  return (
    <ScWrapper className="babys-page-padding babys-page-min-size">
      <FullscreenSpinner show={processing} />
      <ScIntroSentence>
        <h4 className="babys-cursive">Dados de Pagamento</h4>
        <p>Aqui você deve preecher os dados do titular do cartão de crédito.</p>
      </ScIntroSentence>
      <ScForm onSubmit={handleSubmit}>
        <ScInputGroup>
          <label>Nome:</label>
          <Input
            className="max-len"
            onChange={(e) => setName(e.target.value)}
            value={name}
          />
        </ScInputGroup>
        <ScInputGroup>
          <label>Email:</label>
          <Input
            className="max-len"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
          />
        </ScInputGroup>
        <ScInputGroup>
          <label>Endereço: </label>
          <Input
            className="max-len"
            onChange={(e) => setLine1(e.target.value)}
            value={line1}
          />
        </ScInputGroup>
        <ScInputGroup>
          <label>Complemento: </label>
          <Input
            className="max-len"
            onChange={(e) => setLine2(e.target.value)}
            value={line2}
          />
        </ScInputGroup>
        <ScInputGroup>
          <label>País: </label>
          <ScSelect
            value={country}
            onChange={(e) => setCountry(e.currentTarget.value)}
          >
            {countryList.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </ScSelect>
        </ScInputGroup>
        <ScInputGroup>
          <label>Estado / Distrito: </label>
          <Input onChange={(e) => setState(e.target.value)} value={state} />
        </ScInputGroup>
        <ScInputGroup>
          <label>Cidade: </label>
          <Input onChange={(e) => setCity(e.target.value)} value={city} />
        </ScInputGroup>
        <ScInputGroup>
          <label>Cartão de Crédito: </label>
          <ScCardElement
            id="card-element"
            onChange={handleChange}
            options={{
              style: {
                base: {
                  fontSize: '15px',
                  fontFamily:
                    "'Raleway', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                },
              },
            }}
          />
        </ScInputGroup>
        {user && (
          <h5>
            O valor total dos presentes será
            {' ' +
              new Money(
                Object.values(shoppingCart.items).reduce((acc, item) => {
                  acc += item.unitPrice.amount * item.quantity;
                  return acc;
                }, 0),
                user.currency
              ).toString()}
          </h5>
        )}
        <Button
          disabled={
            processing ||
            !(name && city && country && line1 && state && cardCompleted)
          }
          colorSchema={ButtonColorSchema.primary}
          type="submit"
        >
          Confirmar Pagamento
        </Button>
      </ScForm>
    </ScWrapper>
  );
};
